import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import { AlertTriangle, ShieldAlert, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Super-admin-only "Reset to Production Baseline".
 * Wipes every tenant/document, preserving ONLY the current super admin.
 * User must type "RESET" into the confirmation field to enable the button.
 */
const FactoryReset = () => {
  const [confirmText, setConfirmText] = useState('');
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canRun = confirmText === 'RESET' && !running;

  const runReset = async () => {
    if (!canRun) return;
    setRunning(true);
    setLastResult(null);
    try {
      const res = await axios.post(
        `${API}/superadmin/factory-reset`,
        { confirmation: 'RESET' },
        { withCredentials: true }
      );
      setLastResult(res.data);
      toast.success(`Wiped ${res.data.total_deleted} documents.`);
      setConfirmText('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-3xl mx-auto" data-testid="factory-reset-page">
        <header className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <ShieldAlert className="w-8 h-8 text-red-600" />
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              Reset to Production Baseline
            </h1>
          </div>
          <p className="text-slate-600">Wipe every tenant and every record. Only your Super Admin account survives.</p>
        </header>

        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-6" data-testid="factory-reset-warning">
          <div className="flex gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h2 className="font-semibold text-red-900 mb-2">This action is destructive and immediate.</h2>
              <ul className="text-sm text-red-900 space-y-1 list-disc list-inside">
                <li>All companies, users (except you), drivers, and loads will be deleted.</li>
                <li>All payrolls, invoices, notifications, telemetry, audit logs, and documents will be deleted.</li>
                <li>All uploaded files are unaffected (kept on disk), but their database references are removed.</li>
                <li>There is no undo. Take a MongoDB backup first if you might need to restore.</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Type <span className="font-mono font-bold text-red-600">RESET</span> to enable the button
          </label>
          <input
            data-testid="factory-reset-confirm-input"
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="Type RESET"
            className="w-full border border-slate-300 rounded-lg px-4 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-red-500"
            disabled={running}
          />

          <button
            data-testid="factory-reset-execute-btn"
            onClick={runReset}
            disabled={!canRun}
            className={`mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-semibold text-white transition ${
              canRun ? 'bg-red-600 hover:bg-red-700' : 'bg-slate-300 cursor-not-allowed'
            }`}
          >
            {running ? <Loader2 className="w-5 h-5 animate-spin" /> : <ShieldAlert className="w-5 h-5" />}
            {running ? 'Wiping database…' : 'Wipe database now'}
          </button>
        </div>

        {lastResult && (
          <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="factory-reset-result">
            <h3 className="font-semibold text-slate-900 mb-3">Reset complete</h3>
            <p className="text-sm text-slate-700 mb-3">
              Deleted <span className="font-semibold">{lastResult.total_deleted}</span> documents.
              Super admin preserved: <span className="font-semibold">{lastResult.super_admin_email}</span>.
            </p>
            <details className="text-sm">
              <summary className="cursor-pointer text-slate-600 hover:text-slate-900">Per-collection breakdown</summary>
              <table className="mt-3 w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 text-xs text-slate-500 uppercase">
                    <th className="py-1">Collection</th>
                    <th className="py-1 text-right">Deleted</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(lastResult.per_collection || {}).map(([k, v]) => (
                    <tr key={k} className="border-b border-slate-100">
                      <td className="py-1 font-mono text-slate-700">{k}</td>
                      <td className="py-1 text-right font-semibold text-slate-900">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FactoryReset;
